import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../lib/resolveImageUrl';

/** Kompaktes Neuigkeiten-Banner direkt unter dem Hero — für kurzfristige
 *  Ankündigungen (z.B. Partnernetzwerk-Ausbau). Editierbar im Master-Panel
 *  unter Startseite → "Neuigkeiten-Banner". Wird nur gerendert, wenn
 *  aktiviert und ein Titel gesetzt ist — sonst bleibt die Seite unverändert. */
const PartnerNetworkBanner = () => {
  const { getPage } = useSiteSettings();
  const p = getPage('landing');

  if (p.partner_banner_enabled !== true || !p.partner_banner_title) return null;

  const image = resolveImageUrl(p.partner_banner_image);
  const ctaLink = p.partner_banner_cta_link || '/kontakt';
  const ctaText = p.partner_banner_cta_text || 'Jetzt Partner werden';

  return (
    <section className="py-8 sm:py-10 bg-[#0f0f0f]" aria-label="Neuigkeit">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to={ctaLink}
            data-testid="landing-partner-banner"
            className="group relative block overflow-hidden rounded-2xl border border-[#0094ff]/25 bg-gradient-to-r from-[#0a1f33] via-[#0c2540] to-[#082642] hover:border-[#0094ff]/55 transition-all hover:shadow-[0_0_36px_rgba(0,148,255,0.22)]"
          >
            {image && (
              <div className="w-full overflow-hidden">
                <img
                  src={image}
                  alt=""
                  className="w-full h-auto block group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            )}

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-5 sm:p-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#0094ff]/25 text-[#7cc4ff] border border-[#0094ff]/40">
                    Neuigkeit
                  </span>
                  {p.partner_banner_badge && (
                    <span className="text-[10px] uppercase tracking-widest text-[#a1a1aa]">{p.partner_banner_badge}</span>
                  )}
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white leading-tight mb-1">
                  {p.partner_banner_title}
                </h2>
                {p.partner_banner_text && (
                  <p className="text-sm text-[#a1a1aa] max-w-2xl">{p.partner_banner_text}</p>
                )}
              </div>

              <span
                className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-white px-5 py-3 rounded-xl bg-[#0094ff] group-hover:bg-[#33a9ff] transition-colors flex-shrink-0"
                data-testid="landing-partner-banner-cta"
              >
                {ctaText} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
              <span className="sm:hidden inline-flex items-center gap-1 text-sm font-semibold text-[#7cc4ff] mt-1">
                {ctaText} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerNetworkBanner;
